import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { useTranslation } from "../contexts/I18nContext";
import { Loader2, Mail, Lock, User } from "lucide-react";

export default function RegisterPage() {
  const { register } = useAuth();
  const { t } = useTranslation();
  const [form, setForm] = useState({ email: "", password: "", nome: "", cognome: "" });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      showSuccess(t("auth.registrationComplete"));
    } catch (err) {
      handleApiError(err, t("auth.registrationFailed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.7), rgba(0,0,0,0.8)), url('https://images.unsplash.com/photo-1692035072849-93a511f35b2c?w=1920&q=80')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      data-testid="register-page"
    >
      <Card className="w-full max-w-md animate-fade-in">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">{t("auth.register")}</CardTitle>
          <CardDescription>{t("auth.registerSubtitle")}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nome">{t("auth.firstName")}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="nome"
                    name="nome"
                    placeholder="Mario"
                    value={form.nome}
                    onChange={handleChange}
                    className="pl-10"
                    required
                    data-testid="register-nome"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cognome">{t("auth.lastName")}</Label>
                <Input
                  id="cognome"
                  name="cognome"
                  placeholder="Rossi"
                  value={form.cognome}
                  onChange={handleChange}
                  required
                  data-testid="register-cognome"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{t("auth.email")}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="mario@esempio.it"
                  value={form.email}
                  onChange={handleChange}
                  className="pl-10"
                  required
                  data-testid="register-email"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t("auth.password")}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  value={form.password}
                  onChange={handleChange}
                  className="pl-10"
                  required
                  data-testid="register-password"
                />
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {t("auth.register")}
            </Button>
          </form>
          <p className="text-center text-sm text-muted-foreground mt-4">
            {t("auth.haveAccount")}{" "}
            <Link to="/login" className="text-primary hover:underline" data-testid="goto-login">
              {t("auth.login")}
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
